// src/components/site-editor/panels/TestimonialsPanel.js
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Quote, Send } from 'lucide-react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import InlineCallout from '../InlineCallout';
import Toggle from '../../Toggle';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatTestimonialDate = (date) => {
  if (!date) return '';
  const [year, month] = date.split('-');
  const monthName = MONTHS[parseInt(month, 10) - 1];
  return monthName ? `${monthName} ${year}` : date;
};

const TestimonialsPanel = () => {
  const { state, actions } = useWebsiteBuilder();
  const testimonials = state.draft.content.testimonials || [];

  const [editingTestimonial, setEditingTestimonial] = useState(null); // null | 'new' | testimonialId
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleAddTestimonial = () => {
    if (testimonials.length >= 15) {
      return; // Max limit reached
    }
    setEditingTestimonial('new');
  };

  const handleEditTestimonial = (testimonialId) => {
    setEditingTestimonial(testimonialId);
  };

  const handleDeleteTestimonial = (testimonialId) => {
    const updatedTestimonials = testimonials.filter(t => t.id !== testimonialId);
    actions.updateContent('testimonials', updatedTestimonials);
    setDeleteConfirm(null);
  };

  const handleToggleActive = (testimonialId) => {
    const updatedTestimonials = testimonials.map(t =>
      t.id === testimonialId ? { ...t, active: !t.active } : t
    );
    actions.updateContent('testimonials', updatedTestimonials);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Client Testimonials</h3>
          {testimonials.length > 0 && (
            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {testimonials.length} of 15 testimonials
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">
          Share positive feedback from your clients to build trust and credibility. No star ratings needed—authentic words speak louder.
        </p>
      </div>

      {/* Inline Callout */}
      <InlineCallout
        text="Building social proof is essential for growing your practice. Learn strategies for collecting testimonials ethically."
        playbookTopic="growing-your-practice"
      />

      {/* Testimonial List */}
      {testimonials.length > 0 && editingTestimonial === null && (
        <div className="space-y-3">
          {testimonials.map((testimonial) => (
            <TestimonialCard
              key={testimonial.id}
              testimonial={testimonial}
              onEdit={() => handleEditTestimonial(testimonial.id)}
              onDelete={() => setDeleteConfirm(testimonial.id)}
              onToggleActive={() => handleToggleActive(testimonial.id)}
            />
          ))}
        </div>
      )}

      {/* Testimonial Form */}
      {editingTestimonial !== null && (
        <TestimonialForm
          testimonial={editingTestimonial === 'new' ? null : testimonials.find(t => t.id === editingTestimonial)}
          isNew={editingTestimonial === 'new'}
          existingTestimonials={testimonials}
          onSave={(testimonialData) => {
            if (editingTestimonial === 'new') {
              // Add new testimonial
              const newTestimonial = {
                id: `testimonial_${Date.now()}`,
                ...testimonialData,
                order: testimonials.length,
                createdAt: new Date().toISOString()
              };
              actions.updateContent('testimonials', [...testimonials, newTestimonial]);
            } else {
              // Update existing testimonial
              const updatedTestimonials = testimonials.map(t =>
                t.id === editingTestimonial ? { ...t, ...testimonialData } : t
              );
              actions.updateContent('testimonials', updatedTestimonials);
            }
            setEditingTestimonial(null);
          }}
          onCancel={() => setEditingTestimonial(null)}
        />
      )}

      {/* Empty State */}
      {testimonials.length === 0 && editingTestimonial === null && (
        <div className="border border-gray-200 rounded-lg p-8 text-center">
          <Quote className="w-8 h-8 text-[#D1D5DB] mx-auto mb-3" />
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Client Testimonials</h4>
          <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
            Add testimonials to build trust and credibility. Share the kind words your clients have shared with you.
          </p>
          <button
            onClick={handleAddTestimonial}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#7c3aed] rounded-lg text-sm font-medium text-[#7c3aed] hover:bg-[#F5F3FF] hover:border-[#6d28d9] transition-all duration-150"
            style={{ fontSize: '14px', fontWeight: 500 }}
          >
            <Plus className="w-4 h-4" />
            Add Testimonial
          </button>
        </div>
      )}

      {/* Add Testimonial Button (when testimonials exist) */}
      {editingTestimonial === null && testimonials.length > 0 && testimonials.length < 15 && (
        <button
          onClick={handleAddTestimonial}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-[#7c3aed] rounded-lg text-sm font-medium text-[#7c3aed] hover:bg-[#F5F3FF] hover:border-[#6d28d9] transition-all duration-150"
          style={{ fontSize: '14px', fontWeight: 500 }}
        >
          <Plus className="w-4 h-4" />
          Add Testimonial
        </button>
      )}

      {/* Coming Soon Teaser */}
      {editingTestimonial === null && (
        <div className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Send className="w-5 h-5 text-[#9CA3AF] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[13px] font-medium text-gray-700 mb-1">
                Feedback Request Forms — Coming Soon
              </p>
              <p className="text-xs text-[#9CA3AF]">
                Soon you'll be able to send feedback requests directly to clients after sessions
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          clientName={testimonials.find(t => t.id === deleteConfirm)?.clientName}
          onConfirm={() => handleDeleteTestimonial(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ testimonial, onEdit, onDelete, onToggleActive }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-200 transition-colors">
      <div className="flex items-start gap-3">
        {/* Quote Icon */}
        <div className="flex-shrink-0 pt-1">
          <Quote className="w-5 h-5 text-purple-600" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div
            onClick={() => setIsExpanded(!isExpanded)}
            className="cursor-pointer"
          >
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-gray-900">
                {testimonial.clientName}
              </h4>
              {testimonial.serviceReceived && !isExpanded && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="text-xs text-gray-500">{testimonial.serviceReceived}</span>
                </>
              )}
            </div>
            {!isExpanded && (
              <p className="text-xs text-gray-600 line-clamp-2">
                {testimonial.testimonial}
              </p>
            )}
          </div>
          {isExpanded && (
            <>
              <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">
                {testimonial.testimonial}
              </p>
              {(testimonial.serviceReceived || testimonial.date || testimonial.location) && (
                <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                  {testimonial.serviceReceived && <span className="font-medium">{testimonial.serviceReceived}</span>}
                  {testimonial.serviceReceived && (testimonial.date || testimonial.location) && <span>•</span>}
                  {testimonial.date && <span>{formatTestimonialDate(testimonial.date)}</span>}
                  {testimonial.date && testimonial.location && <span>•</span>}
                  {testimonial.location && <span>{testimonial.location}</span>}
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Toggle
            enabled={testimonial.active}
            onChange={onToggleActive}
          />
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Testimonial Form Component
const TestimonialForm = ({ testimonial, isNew, existingTestimonials, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    clientName: testimonial?.clientName || '',
    testimonial: testimonial?.testimonial || '',
    serviceReceived: testimonial?.serviceReceived || '',
    date: testimonial?.date || new Date().toISOString().slice(0, 7), // YYYY-MM format for month input
    location: testimonial?.location || '',
    active: testimonial?.active ?? true
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const textareaRef = useRef(null);

  const MAX_CLIENT_NAME_LENGTH = 50;
  const MAX_TESTIMONIAL_LENGTH = 500;
  const MAX_SERVICE_LENGTH = 100;
  const MAX_LOCATION_LENGTH = 50;

  // Auto-expand textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(80, textarea.scrollHeight) + 'px';
    }
  }, [formData.testimonial]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    } else if (formData.clientName.length > MAX_CLIENT_NAME_LENGTH) {
      newErrors.clientName = `Client name must be ${MAX_CLIENT_NAME_LENGTH} characters or less`;
    }

    if (!formData.testimonial.trim()) {
      newErrors.testimonial = 'Testimonial text is required';
    } else if (formData.testimonial.length > MAX_TESTIMONIAL_LENGTH) {
      newErrors.testimonial = `Testimonial must be ${MAX_TESTIMONIAL_LENGTH} characters or less`;
    }

    if (formData.location.length > MAX_LOCATION_LENGTH) {
      newErrors.location = `Location must be ${MAX_LOCATION_LENGTH} characters or less`;
    }

    if (formData.serviceReceived.length > MAX_SERVICE_LENGTH) {
      newErrors.serviceReceived = `Service name must be ${MAX_SERVICE_LENGTH} characters or less`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      setTouched({
        clientName: true,
        testimonial: true,
        serviceReceived: true,
        location: true
      });
      return;
    }

    onSave(formData);
  };

  const clientNameLength = formData.clientName.length;
  const testimonialLength = formData.testimonial.length;
  const serviceLength = formData.serviceReceived.length;
  const locationLength = formData.location.length;

  const isClientNameOverLimit = clientNameLength > MAX_CLIENT_NAME_LENGTH;
  const isTestimonialOverLimit = testimonialLength > MAX_TESTIMONIAL_LENGTH;
  const isServiceOverLimit = serviceLength > MAX_SERVICE_LENGTH;
  const isLocationOverLimit = locationLength > MAX_LOCATION_LENGTH;
  const isTestimonialNearLimit = testimonialLength > MAX_TESTIMONIAL_LENGTH * 0.9;

  return (
    <form onSubmit={handleSubmit} className="border border-purple-200 rounded-lg p-5 bg-purple-50/30 space-y-4">
      <h4 className="text-sm font-semibold text-gray-900">
        {isNew ? 'Add Testimonial' : 'Edit Testimonial'}
      </h4>

      {/* Client Name Field */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Client Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.clientName}
          onChange={(e) => handleChange('clientName', e.target.value)}
          onBlur={() => handleBlur('clientName')}
          placeholder="e.g., Sarah M."
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
            (errors.clientName && touched.clientName) || isClientNameOverLimit
              ? 'border-red-400'
              : 'border-gray-300'
          }`}
        />
        {errors.clientName && touched.clientName && (
          <p className="mt-1 text-xs text-red-600">{errors.clientName}</p>
        )}
        <p className={`mt-1 text-xs ${
          isClientNameOverLimit ? 'text-red-600 font-medium' : 'text-gray-500'
        }`}>
          {clientNameLength}/{MAX_CLIENT_NAME_LENGTH} characters
          {isClientNameOverLimit && ` (-${clientNameLength - MAX_CLIENT_NAME_LENGTH} over limit)`}
        </p>
      </div>

      {/* Their Words Text Field */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Their Words <span className="text-red-500">*</span>
        </label>
        <textarea
          ref={textareaRef}
          value={formData.testimonial}
          onChange={(e) => handleChange('testimonial', e.target.value)}
          onBlur={() => handleBlur('testimonial')}
          placeholder="Share what your client said about working with you..."
          style={{ minHeight: '80px', resize: 'vertical' }}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
            (errors.testimonial && touched.testimonial) || isTestimonialOverLimit
              ? 'border-red-400'
              : 'border-gray-300'
          }`}
        />
        {errors.testimonial && touched.testimonial && (
          <p className="mt-1 text-xs text-red-600">{errors.testimonial}</p>
        )}

        {/* Character Counter with Progress Bar */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <p className={`text-xs ${
              isTestimonialOverLimit
                ? 'text-red-600 font-medium'
                : isTestimonialNearLimit
                ? 'text-amber-600 font-medium'
                : 'text-gray-500'
            }`}>
              {testimonialLength}/{MAX_TESTIMONIAL_LENGTH} characters
              {isTestimonialOverLimit && ` (-${testimonialLength - MAX_TESTIMONIAL_LENGTH} over limit)`}
            </p>
          </div>
          <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                isTestimonialOverLimit
                  ? 'bg-red-500'
                  : isTestimonialNearLimit
                  ? 'bg-amber-500'
                  : 'bg-purple-500'
              }`}
              style={{ width: `${Math.min((testimonialLength / MAX_TESTIMONIAL_LENGTH) * 100, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Service Received Field */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Service Received
        </label>
        <input
          type="text"
          value={formData.serviceReceived}
          onChange={(e) => handleChange('serviceReceived', e.target.value)}
          onBlur={() => handleBlur('serviceReceived')}
          placeholder="e.g., Tarot Reading, Reiki"
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
            (errors.serviceReceived && touched.serviceReceived) || isServiceOverLimit
              ? 'border-red-400'
              : 'border-gray-300'
          }`}
        />
        {errors.serviceReceived && touched.serviceReceived && (
          <p className="mt-1 text-xs text-red-600">{errors.serviceReceived}</p>
        )}
        <p className={`mt-1 text-xs ${
          isServiceOverLimit ? 'text-red-600 font-medium' : 'text-gray-500'
        }`}>
          Helps visitors see which services people love • {serviceLength}/{MAX_SERVICE_LENGTH} characters
        </p>
      </div>

      {/* Date and Location Row */}
      <div className="grid grid-cols-2 gap-3">
        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Date
          </label>
          <div className="flex flex-col gap-1">
            <select
              value={formData.date ? formData.date.split('-')[1] : ''}
              onChange={(e) => {
                const year = formData.date ? formData.date.split('-')[0] : new Date().getFullYear().toString();
                handleChange('date', e.target.value ? `${year}-${e.target.value}` : '');
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Month</option>
              {[['01','Jan'],['02','Feb'],['03','Mar'],['04','Apr'],['05','May'],['06','Jun'],['07','Jul'],['08','Aug'],['09','Sep'],['10','Oct'],['11','Nov'],['12','Dec']].map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
            <select
              value={formData.date ? formData.date.split('-')[0] : ''}
              onChange={(e) => {
                const month = formData.date ? formData.date.split('-')[1] : '01';
                handleChange('date', e.target.value ? `${e.target.value}-${month}` : '');
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Year</option>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y.toString()}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Location
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            onBlur={() => handleBlur('location')}
            placeholder="Austin, TX"
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
              (errors.location && touched.location) || isLocationOverLimit
                ? 'border-red-400'
                : 'border-gray-300'
            }`}
          />
          {errors.location && touched.location && (
            <p className="mt-1 text-xs text-red-600">{errors.location}</p>
          )}
          <p className={`mt-1 text-xs ${
            isLocationOverLimit ? 'text-red-600 font-medium' : 'text-gray-500'
          }`}>
            {locationLength}/{MAX_LOCATION_LENGTH} characters
          </p>
        </div>
      </div>

      {/* Active Toggle */}
      <div className="flex items-center justify-between p-3 bg-white rounded-lg">
        <div>
          <p className="text-sm font-medium text-gray-900">Display on Website</p>
          <p className="text-xs text-gray-600">Toggle off to hide this testimonial without deleting it</p>
        </div>
        <Toggle
          enabled={formData.active}
          onChange={(value) => handleChange('active', value)}
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isClientNameOverLimit || isTestimonialOverLimit || isServiceOverLimit || isLocationOverLimit}
          className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isNew ? 'Add Testimonial' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ clientName, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-sm w-full p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Testimonial?</h3>
        <p className="text-sm text-gray-600 mb-1">Are you sure you want to delete the testimonial from:</p>
        <p className="text-sm font-semibold text-gray-900 mb-6">"{clientName}"</p>
        <p className="text-xs text-gray-500 mb-6">This cannot be undone.</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsPanel;
